import { ArrowLeft, Loader2 } from "lucide-react";

import { LocalImplicationsPane } from "@/components/legislation_monitor/LocalImplicationsPane";
import { StatutoryChangesPane } from "@/components/legislation_monitor/StatutoryChangesPane";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useUpcomingLawDetail } from "@/hooks/legislationMonitor/useUpcomingLawsSearch";
import { formatUpcomingLawDisplayName } from "@/lib/legislationMonitorFormat";

type UpcomingLawDetailProps = {
  lawId: number;
  municipalityId?: number | null;
  municipalityName: string;
  onBack: () => void;
};

export function UpcomingLawDetail({
  lawId,
  municipalityId,
  municipalityName,
  onBack,
}: UpcomingLawDetailProps) {
  const {
    law,
    statutoryAnalysis,
    municipalImplicationsEligible,
    municipalImplications,
    loading,
    error,
  } = useUpcomingLawDetail(lawId, municipalityId);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden />
        Loading upcoming law…
      </div>
    );
  }

  if (error || !law) {
    return (
      <div className="space-y-4 py-8 text-center">
        <p className="text-muted-foreground">{error ?? "Upcoming law not found."}</p>
        <Button variant="outline" onClick={onBack}>
          Back to upcoming laws
        </Button>
      </div>
    );
  }

  const displayName = formatUpcomingLawDisplayName(law.name);
  const overviewSummary = law.description?.trim() || "No summary available";

  return (
    <div className="space-y-8">
      <Button variant="ghost" className="px-0" onClick={onBack}>
        <ArrowLeft className="mr-2 h-4 w-4" aria-hidden />
        Back to upcoming laws
      </Button>

      <section>
        <h2 className="text-2xl font-semibold text-foreground">{displayName.primary}</h2>
      </section>

      <Accordion type="multiple" defaultValue={["overview"]} className="w-full">
        <AccordionItem value="overview">
          <AccordionTrigger className="text-base font-semibold hover:no-underline">
            Overview
          </AccordionTrigger>
          <AccordionContent>
            <p className="text-sm leading-relaxed text-muted-foreground">{overviewSummary}</p>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="state-amendments">
          <AccordionTrigger className="text-base font-semibold hover:no-underline">
            Statutory Changes
          </AccordionTrigger>
          <AccordionContent>
            <StatutoryChangesPane
              analysis={statutoryAnalysis}
              failedMessage="Statutory analysis could not be generated. Ordinal staff can try again."
            />
          </AccordionContent>
        </AccordionItem>

        {municipalImplicationsEligible ? (
          <AccordionItem value="municipal-impact">
            <AccordionTrigger className="text-base font-semibold hover:no-underline">
              Local Implications
            </AccordionTrigger>
            <AccordionContent>
              <LocalImplicationsPane
                analysis={municipalImplications}
                notAvailableMessage={`A local review for ${municipalityName} is not ready yet.`}
              />
            </AccordionContent>
          </AccordionItem>
        ) : null}
      </Accordion>
    </div>
  );
}
